/**
 * RhPaiePage — consolidation RH des plannings pour la paie.
 *
 * Sélection d'une période → tableau des heures planifiées par agent, tous
 * services confondus (technique + scolaire), avec détail par service et part
 * déjà transmise à la paie. Lecture seule (permission rh.paie.read).
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Card, Button, Field, Input, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useModeValidationConges } from '@/lib/use-mode-validation-conges';
import { api, ApiException, type DemandeFormationDTO } from '@/lib/api';
import { Clock, Send, ArrowLeft } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import type { RhConsolidationResult, RhParametres, RhSoldeConges, RhHeuresSup, RhHeuresSupCompteur, RhAbsence, RhHoraireModifie } from '@roronoa-collectivites/shared-types';
import { RhStats, RhWho, RhServicePill } from './rh-ui';
import { RhCloturePanel } from './RhCloturePanel';
import { RhParametresCloturePanel } from './RhParametresCloturePanel';
import { RhHabilitationsPanel } from './RhHabilitationsPanel';
import { RhVisitesMedicalesPanel } from './RhVisitesMedicalesPanel';
import { RhDossiersAgentsPanel } from './RhDossiersAgentsPanel';
import { RhReferentielsPanel } from './RhReferentielsPanel';
import { RhOrganigrammePanel } from './RhOrganigrammePanel';
import { RhFormationsPanel } from './RhFormationsPanel';
import { RhArretesPanel } from './RhArretesPanel';
import { RhPlanningsPanel } from './RhPlanningsPanel';
import { RhExportPanel } from './RhExportPanel';
import { RhProfilsPanel } from './RhProfilsPanel';
import { RhDocumentsPanel } from './RhDocumentsPanel';
import { RhNotesFraisPanel } from './RhNotesFraisPanel';
import { RhNotesFraisAValider } from './RhNotesFraisAValider';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { RhJustificatifsRecus } from './RhJustificatifsRecus';
import { RhTransmettreArret } from './RhTransmettreArret';

const SERVICE_LABELS: Record<string, string> = {
  service_technique: 'Technique',
  scolaire_cantine: 'Scolaire / cantine',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  associations: 'Associations',
  grc: 'Communication',
  rh: 'Ressources humaines',
};

const ROLE_LABELS_RH: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Maire / adjoint',
  manager: 'Responsable',
  agent: 'Agent',
};

function lundiDeCetteSemaine(): string {
  const d = new Date();
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}
function dimancheDeCetteSemaine(): string {
  const d = new Date();
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() + (7 - day));
  return d.toISOString().slice(0, 10);
}

import { RhNav, type RhTab } from './RhNav';
import { RhMonPlanningPanel } from './RhMonPlanningPanel';
import { RhMonDossierPanel } from './RhMonDossierPanel';
import { rafraichirNotifications } from '@/components/NotificationBell';
import { MesDeplacementsCard } from '@/components/rh/MesDeplacementsCard';
import { ReglagesContenu } from '@/pages/reglages/ReglagesBriquePage';

export function RhPaiePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Vue « administré » de la RH (déclarer ses congés/absences à titre personnel) :
  // /rh?vue=perso force l'onglet « Mes demandes », y compris pour maire/adjoint/
  // manager qui atterrissent sinon sur la vue gestion.
  const vuePerso = searchParams.get('vue') === 'perso';
  // Arbitrage B (20/08) : la vue GESTION suit la même règle que la tuile du
  // tableau de bord — admin/maire, ou manager DU SERVICE RH (principal ou
  // cumulé). Un manager d'un autre service tapait `/rh` à la main et obtenait
  // la gestion complète (plannings, congés, documents de tout le monde) que la
  // tuile lui refusait : la porte était fermée, pas la pièce. Sa validation N1
  // vit désormais dans SA vue perso, onglet Notes de frais.
  const managerRh = user?.role === 'manager' && (
    user.service === 'rh' || (user.servicesAdditionnels ?? []).includes('rh')
  );
  const isGestion = !vuePerso && (user?.role === 'admin' || user?.role === 'super_admin' || managerRh);

  // Vague A2 — ouverture d'un onglet par l'URL : `/rh?vue=perso&onglet=notesfrais`.
  // Sans ça, « Mon espace agent » ne pouvait pointer que sur la page RH en vrac,
  // et l'agent devait retrouver son onglet à la main. Les onglets de gestion ne
  // sont PAS ouvrables ainsi : un agent qui bricolerait l'URL n'obtiendrait rien
  // de plus, la garde reste côté rendu et côté API.
  // Onglets ouvrables par l'URL. Ceux des AGENTS le sont toujours ; ceux de
  // GESTION seulement pour qui est en vue gestion — c'est ce qui permet à la
  // cloche « Arrêté à signer » de tomber au bon endroit (D-02) sans qu'un agent
  // puisse s'y inviter en bricolant l'adresse. Le rendu reste gardé derrière.
  const ONGLETS_AGENT: readonly RhTab[] = ['mesdemandes', 'monplanning', 'notesfrais', 'documents', 'mondossier'];
  // Refonte UX (irritant n° 2, 24/09) : la liste ne contenait que 5 des 11 onglets de
  // gestion — formations, habilitations, référentiels, organigramme, clôture et
  // dossiers agents ne s'ouvraient jamais par un lien (notifications, présentation
  // client…). Ajoutés, TOUJOURS réservés à la vue gestion ; rendu et serveur gardés.
  const ONGLETS_GESTION: readonly RhTab[] = ['arretes', 'avalider', 'plannings', 'conges', 'documents',
    'cloture', 'dossiersagents', 'referentiels', 'organigramme', 'formations', 'habilitations', 'parametres'];
  const ongletDemande = searchParams.get('onglet') as RhTab | null;
  const ongletAutorise = ongletDemande
    ? ONGLETS_AGENT.includes(ongletDemande) || (isGestion && ONGLETS_GESTION.includes(ongletDemande))
    : false;
  const ongletInitial: RhTab | null = ongletAutorise ? ongletDemande : null;

  const [tab, setTab] = useState<RhTab>(ongletInitial ?? (isGestion ? 'avalider' : 'mesdemandes'));
  // Si l'utilisateur bascule entre vue gestion (/rh) et vue perso (/rh?vue=perso)
  // sans remontage du composant, on réaligne l'onglet actif sur le mode courant.
  useEffect(() => {
    setTab(ongletInitial ?? (isGestion ? 'avalider' : 'mesdemandes'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vuePerso, ongletDemande]);
  const [from, setFrom] = useState(lundiDeCetteSemaine());
  const [to, setTo] = useState(dimancheDeCetteSemaine());
  const [transmisSeulement, setTransmisSeulement] = useState(false);
  const [data, setData] = useState<RhConsolidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const TITRES: Record<RhTab, string> = {
    mesdemandes: 'Mes demandes', monplanning: 'Mon planning', plannings: 'Plannings', avalider: 'À valider', cloture: 'Paie & clôture', paie: 'Consolidation paie',
    organigramme: 'Organigramme & langues',
    formations: 'Formations', arretes: 'Arrêtés',
    export: 'Export & API', conges: 'Congés', heuressup: 'Heures supplémentaires', absences: 'Absences', profils: 'Profils horaires', documents: 'Documents', notesfrais: 'Notes de frais', mondossier: 'Mon dossier', habilitations: 'Habilitations & suivi médical', dossiersagents: 'Dossiers agents', referentiels: 'Référentiels RH', parametres: 'Paramètres',
  };

  async function charger() {
    setLoading(true); setError(null);
    try {
      setData(await api.rh.consoliderPaie(from, to, transmisSeulement));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Ressources humaines" accent="rh" />
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 space-y-5">
      <div className="flex items-center gap-3">
        <span className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-rh-tint text-rh-fg" aria-hidden="true">
          <Clock className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Ressources humaines</p>
          <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
            {TITRES[tab]}
          </h1>
        </div>
      </div>

      {/* Onglets regroupés (menus déroulants, cohérent Comptabilité/GRC) */}
      <RhNav tab={tab} setTab={setTab} estGestion={isGestion} />

      {tab === 'monplanning' ? (
        /* Planning individuel — toutes briques confondues, filtré côté serveur
           sur l'utilisateur connecté. Cliquer des jours puis « Déclarer une
           absence » alimente le workflow de validation existant (mig 196). */
        <RhMonPlanningPanel />
      ) : tab === 'mesdemandes' ? (
        <div className="space-y-3">
          {/* M26 (retour Manon, mi-parcours) : dire ce qu'on peut demander ici —
              l'onglet était muet, un nouvel agent ne savait pas quoi y chercher. */}
          <p className="text-sm text-ink-muted">
            C'est ici que vous déposez vos demandes à la mairie : congés annuels,
            RTT, absences (maladie, garde d'enfant, événement familial…),
            récupérations. Votre responsable est prévenu et vous suivez la
            réponse au même endroit.
          </p>
          <MesDemandesPanel />
        </div>
      ) : tab === 'plannings' ? (
        <RhPlanningsPanel />
      ) : tab === 'avalider' ? (
        <RhValiderPanel onGoTo={setTab} />
      ) : tab === 'cloture' ? (
        <RhCloturePanel />
      ) : tab === 'dossiersagents' ? (
        <RhDossiersAgentsPanel />
      ) : tab === 'mondossier' ? (
        <RhMonDossierPanel />
      ) : tab === 'referentiels' ? (
        <RhReferentielsPanel />
      ) : tab === 'organigramme' ? (
        <RhOrganigrammePanel />
      ) : tab === 'formations' ? (
        <RhFormationsPanel />
      ) : tab === 'arretes' ? (
        <RhArretesPanel />
      ) : tab === 'habilitations' ? (
        <>
          {/*
            Visites médicales sous le même onglet que les habilitations : c'est
            le même geste RH — suivre des échéances individuelles par agent —
            et ça évite un onglet de plus pour deux tableaux.
          */}
          <RhHabilitationsPanel />
          <div className="mt-6">
            <RhVisitesMedicalesPanel />
          </div>
        </>
      ) : tab === 'parametres' ? (
        <div className="space-y-6">
          <RhParametresCloturePanel />
          {/* Recette du 28/09 : les réglages RH au même endroit. */}
          <ReglagesContenu brique="rh" />
        </div>
      ) : tab === 'export' ? (
        <RhExportPanel />
      ) : tab === 'profils' ? (
        <RhProfilsPanel />
      ) : tab === 'documents' ? (
        <RhDocumentsPanel />
      ) : tab === 'notesfrais' ? (
        <div className="space-y-5">
          {/* Arbitrage B (20/08) : TOUT manager valide les notes de SON service
              depuis sa vue perso — y compris hors service RH, à qui la vue
              gestion est fermée. Le panneau se tait quand la file est vide, et
              l'API filtre déjà sur le service du manager. La cloche
              « ndf_a_valider » pointe ici. */}
          {user?.role === 'manager' && <RhNotesFraisAValider />}
          <RhNotesFraisPanel />
        </div>
      ) : tab === 'conges' ? (
        <RhCongesPanel />
      ) : tab === 'heuressup' ? (
        <RhHeuresSupPanel />
      ) : tab === 'absences' ? (
        <RhAbsencesPanel />
      ) : (
      <>
      <p className="text-sm text-ink-muted">
        Heures planifiées par agent sur une période, tous services confondus
        (technique, scolaire, communication). La part « transmise » correspond aux
        semaines envoyées à la paie depuis chaque service.
      </p>

      {error && <Alert variant="error">{error}</Alert>}

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Du" htmlFor="rh-from"><Input id="rh-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Au" htmlFor="rh-to"><Input id="rh-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          <label className="flex items-center gap-1.5 pb-2 text-sm text-ink-muted">
            <input type="checkbox" checked={transmisSeulement} onChange={(e) => setTransmisSeulement(e.target.checked)} />
            Transmis à la paie uniquement
          </label>
          <Button variant="rh" loading={loading} onClick={charger}>Consolider</Button>
        </div>
      </Card>

      {data && (
        <Card className="p-4">
          {data.agents.length === 0 ? (
            <p className="text-sm text-ink-subtle">Aucune heure planifiée sur cette période.</p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-base font-semibold text-ink">
                  {data.agents.length} agent{data.agents.length > 1 ? 's' : ''}
                </h2>
                <Badge variant="rh">{data.totalHeures} h au total</Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                      <th className="py-2 pr-3">Agent</th>
                      <th className="py-2 pr-3">Détail par service</th>
                      <th className="py-2 pr-3 text-right">Transmis</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agents.map((a) => (
                      <tr key={a.agentUserId} className="border-b border-border/60">
                        <td className="py-2 pr-3 font-medium text-ink">{a.agentPrenom} {a.agentNom}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1.5">
                            {a.parService.map((s) => (
                              <span key={s.service} className="inline-flex items-center gap-1 rounded-r-pill border border-border px-2 py-0.5 text-xs text-ink-muted">
                                {SERVICE_LABELS[s.service] ?? s.service} · {s.heures} h
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">
                          <span className="inline-flex items-center gap-1"><Send className="h-3 w-3" />{a.heuresTransmises} h</span>
                        </td>
                        <td className="py-2 text-right font-semibold tabular-nums text-ink">{a.totalHeures} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
      </>
      )}
      </div>
    </div>
  );
}

// ============================================================================
// Panneau Congés : paramètres RH + soldes par agent
// ============================================================================
function RhCongesPanel() {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [params, setParams] = useState<RhParametres | null>(null);
  const [soldes, setSoldes] = useState<RhSoldeConges[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Soldes initiaux compteurs heures (RTT / récup HS / récup férié) par agent
  const [cptDrafts, setCptDrafts] = useState<Record<string, { rtt: string; recup_hs: string; recup_ferie: string }>>({});

  async function charger(an = annee) {
    setLoading(true); setError(null);
    try {
      const res = await api.rh.getSoldes(an);
      setParams(res.parametres);
      setSoldes(res.soldes);
      const d: Record<string, string> = {};
      res.soldes.forEach((s) => { d[s.agentUserId] = String(s.soldeInitial); });
      setDrafts(d);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(anneeCourante); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function enregistrerParams(patch: Partial<RhParametres>) {
    if (!params) return;
    setError(null); setMsg(null);
    try {
      const next = await api.rh.updateParametres({
        baseConges: patch.baseConges ?? params.baseConges,
        modeDecompte: patch.modeDecompte ?? params.modeDecompte,
        heuresParJour: patch.heuresParJour ?? params.heuresParJour,
      });
      setParams(next);
      setMsg('Paramètres enregistrés.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function enregistrerSolde(agentUserId: string) {
    const val = Number(drafts[agentUserId]);
    if (Number.isNaN(val) || val < 0) { setError('Solde invalide'); return; }
    setError(null); setMsg(null);
    try {
      await api.rh.setSolde({ agentUserId, annee, soldeInitial: val });
      setMsg('Solde enregistré.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function enregistrerCompteur(agentUserId: string, type: 'rtt' | 'recup_hs' | 'recup_ferie') {
    const raw = cptDrafts[agentUserId]?.[type] ?? '';
    const val = Number(raw);
    if (Number.isNaN(val) || val < 0) { setError('Valeur invalide'); return; }
    setError(null); setMsg(null);
    try {
      await api.rh.setSoldeInitial({ agentUserId, annee, type, soldeInitial: val });
      setMsg('Solde initial enregistré.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const uniteLabel = params?.modeDecompte === 'heure' ? 'h' : 'j';

  // Stats agrégées pour la bande d'en-tête
  const nbAgents = soldes.length;
  const soldeMoyen = nbAgents > 0 ? Math.round((soldes.reduce((s, x) => s + x.restant, 0) / nbAgents) * 10) / 10 : 0;
  const soldesFaibles = soldes.filter((s) => s.restant < 5).length;

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <RhStats items={[
        { label: 'Agents', value: nbAgents },
        { label: 'Solde moyen', value: `${soldeMoyen} ${uniteLabel}`, tone: 'ok' },
        { label: 'Soldes faibles', value: soldesFaibles, sub: `< 5 ${uniteLabel} restants`, tone: soldesFaibles > 0 ? 'warn' : 'default' },
        { label: 'Base FPT', value: `${params?.joursParAn ?? 25} j`, sub: 'jours ouvrés', tone: 'rh' },
      ]} />

      {/* Paramètres */}
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-ink">Paramètres</h2>
        {params && (
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="mb-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Base annuelle</div>
              <div className="flex h-11 items-center rounded-r-lg border border-border bg-surface/40 px-3.5 text-sm text-ink">
                25 jours ouvrés / an <span className="ml-1.5 text-2xs text-ink-subtle">(règle FPT)</span>
              </div>
            </div>
            <Field label="Décompte" htmlFor="rh-mode">
              <select id="rh-mode" value={params.modeDecompte} onChange={(e) => void enregistrerParams({ modeDecompte: e.target.value as RhParametres['modeDecompte'] })}
                className="h-11 rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="jour">En jours entiers</option>
                <option value="demi_jour">En demi-journées</option>
                <option value="heure">En heures (cycles variables)</option>
              </select>
            </Field>
            <Badge variant="rh">{params.joursParAn} j / an</Badge>
          </div>
        )}
        <p className="mt-3 text-xs text-ink-subtle">
          <strong>Règle FPT :</strong> les congés se décomptent en <strong>jours ouvrés</strong> (5 × obligations hebdomadaires, soit 25 j/an pour un temps plein sur 5 jours), arrondis à la demi-journée supérieure. Le décompte <strong>en heures est réservé</strong> aux agents à cycles/horaires variables (été/hiver, annualisation) ; il est jugé illégal comme règle générale. En cas de doute, garder « jours ».
        </p>
      </Card>

      {/* Soldes par agent */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-ink">Soldes {annee}</h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { const a = annee - 1; setAnnee(a); void charger(a); }} className="rounded-r-md border border-border px-2 py-1 text-sm text-ink-muted hover:text-ink">←</button>
            <span className="text-sm font-medium text-ink tabular-nums">{annee}</span>
            <button type="button" onClick={() => { const a = annee + 1; setAnnee(a); void charger(a); }} className="rounded-r-md border border-border px-2 py-1 text-sm text-ink-muted hover:text-ink">→</button>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : soldes.length === 0 ? (
          <p className="text-sm text-ink-subtle">Aucun agent.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 pr-3">Droits acquis ({uniteLabel})</th>
                  <th className="py-2 pr-3 text-right">Consommé</th>
                  <th className="py-2 pr-3 text-right">Restant</th>
                  <th className="py-2 pr-3" style={{ width: 180 }}>Consommation</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {soldes.map((s) => {
                  const acquis = s.consomme + s.restant;
                  const pct = acquis > 0 ? Math.min(100, Math.round((s.consomme / acquis) * 100)) : 0;
                  const barColor = pct >= 80 ? 'var(--c-warn, #d99a28)' : pct >= 100 ? 'var(--c-danger, #d8583a)' : 'var(--c-success, #3fae74)';
                  return (
                  <tr key={s.agentUserId} className="border-b border-border/60">
                    <td className="py-2 pr-3"><RhWho nom={`${s.agentPrenom} ${s.agentNom}`} /></td>
                    <td className="py-2 pr-3">
                      <input type="number" min={0} step={params?.modeDecompte === 'demi_jour' ? 0.5 : 1}
                        value={drafts[s.agentUserId] ?? ''} onChange={(e) => setDrafts((d) => ({ ...d, [s.agentUserId]: e.target.value }))}
                        className="h-9 w-24 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">{s.consomme} {uniteLabel}</td>
                    <td className="py-2 pr-3 text-right font-semibold tabular-nums" style={{ color: pct >= 80 ? 'var(--c-warn, #d99a28)' : undefined }}>{s.restant} {uniteLabel}</td>
                    <td className="py-2 pr-3">
                      <div className="h-[7px] w-full overflow-hidden rounded-r-pill bg-surface/60">
                        <div className="h-full rounded-r-pill" style={{ width: `${pct}%`, background: barColor }} />
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <Button variant="secondary" size="sm" onClick={() => void enregistrerSolde(s.agentUserId)}>Enregistrer</Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-ink-subtle">Les congés payés validés viendront se décompter automatiquement du solde (lot suivant). Les jours fériés ne décomptent pas, sauf décision du manager.</p>
      </Card>

      {/* Soldes initiaux RTT / récupération (import de l'existant) */}
      <Card className="p-4">
        <h2 className="mb-1 font-display text-base font-semibold text-ink">Compteurs initiaux {annee} — RTT & récupération</h2>
        <p className="mb-3 text-xs text-ink-muted">À la mise en route, saisis ici les compteurs que chaque agent avait déjà dans l'ancien système (en heures). L'application cumule ensuite à partir de ces valeurs.</p>
        {soldes.length === 0 ? (
          <p className="text-sm text-ink-subtle">Aucun agent.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 pr-3">RTT (h)</th>
                  <th className="py-2 pr-3">Récup. heures sup (h)</th>
                  <th className="py-2 pr-3">Récup. férié (h)</th>
                </tr>
              </thead>
              <tbody>
                {soldes.map((s) => {
                  const d = cptDrafts[s.agentUserId] ?? { rtt: '', recup_hs: '', recup_ferie: '' };
                  const setD = (patch: Partial<typeof d>) => setCptDrafts((prev) => ({ ...prev, [s.agentUserId]: { ...d, ...patch } }));
                  const champ = (type: 'rtt' | 'recup_hs' | 'recup_ferie', value: string) => (
                    <div className="flex items-center gap-1">
                      <input type="number" min={0} step={0.5} value={value}
                        onChange={(e) => setD({ [type]: e.target.value } as Partial<typeof d>)}
                        className="h-9 w-20 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
                      <button type="button" onClick={() => void enregistrerCompteur(s.agentUserId, type)}
                        className="rounded-r-md border border-border-hi bg-bg-2/60 px-2 py-1 text-xs text-ink-muted hover:text-ink">OK</button>
                    </div>
                  );
                  return (
                    <tr key={s.agentUserId} className="border-b border-border/60">
                      <td className="py-2 pr-3 font-medium text-ink">{s.agentPrenom} {s.agentNom}</td>
                      <td className="py-2 pr-3">{champ('rtt', d.rtt)}</td>
                      <td className="py-2 pr-3">{champ('recup_hs', d.recup_hs)}</td>
                      <td className="py-2 pr-3">{champ('recup_ferie', d.recup_ferie)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Panneau Heures supplémentaires : à valider + compteurs
// ============================================================================
function fmtMin(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60;
  if (h === 0 && m === 0) return '0';
  return `${h > 0 ? `${h}h` : ''}${m > 0 ? (h > 0 ? String(m).padStart(2, '0') : `${m}min`) : (h > 0 ? '00' : '')}`;
}

function RhHeuresSupPanel() {
  const [aValider, setAValider] = useState<RhHeuresSup[]>([]);
  const [compteurs, setCompteurs] = useState<RhHeuresSupCompteur[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function charger() {
    setLoading(true); setError(null);
    try {
      const [liste, cpt] = await Promise.all([
        api.rh.listerHeuresSup('en_attente'),
        api.rh.compteursHeuresSup(),
      ]);
      setAValider(liste); setCompteurs(cpt);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(); }, []);

  async function statuer(it: RhHeuresSup, statut: 'valide' | 'refuse', destination?: 'paie' | 'rtt') {
    setError(null); setMsg(null);
    try {
      await api.rh.statuerHeuresSup(it.id, { statut, destination });
      setMsg(statut === 'valide' ? 'Heures validées.' : 'Heures refusées.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const totMin = (sel: (c: RhHeuresSupCompteur) => number) => compteurs.reduce((s, c) => s + sel(c), 0);

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <RhStats items={[
        { label: 'À valider', value: aValider.length, tone: aValider.length > 0 ? 'rh' : 'default' },
        { label: 'Payées (total)', value: fmtMin(totMin((c) => c.minutesPaie)) },
        { label: 'Basculées RTT', value: fmtMin(totMin((c) => c.minutesRtt)) },
        { label: 'En attente', value: fmtMin(totMin((c) => c.minutesEnAttente)) },
      ]} />
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-ink">À valider {aValider.length > 0 && <Badge variant="rh">{aValider.length}</Badge>}</h2>
        {loading ? <p className="text-sm text-ink-muted">Chargement…</p>
          : aValider.length === 0 ? <p className="text-sm text-ink-subtle">Aucune heure supplémentaire en attente.</p>
          : (
            <ul className="space-y-2">
              {aValider.map((it) => (
                <li key={it.id} className="flex flex-wrap items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-ink">{it.agentNom} · {new Date(it.dateJour).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div>
                    <div className="text-xs text-ink-muted">
                      {it.minutesMatin > 0 && `Matin ${fmtMin(it.minutesMatin)}`}
                      {it.minutesMatin > 0 && it.minutesApresMidi > 0 && ' · '}
                      {it.minutesApresMidi > 0 && `Après-midi ${fmtMin(it.minutesApresMidi)}`}
                      {' · total '}<strong>{fmtMin(it.totalMinutes)}</strong>
                      {it.motif && ` — ${it.motif}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="secondary" size="sm" onClick={() => void statuer(it, 'valide', 'paie')}>Valider → paie</Button>
                    <Button variant="secondary" size="sm" onClick={() => void statuer(it, 'valide', 'rtt')}>Valider → RTT</Button>
                    <button type="button" onClick={() => void statuer(it, 'refuse')} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Card>

      {/* Compteurs */}
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-ink">Compteurs par agent</h2>
        {compteurs.length === 0 ? <p className="text-sm text-ink-subtle">Aucune donnée.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 pr-3 text-right">Validées</th>
                  <th className="py-2 pr-3 text-right">→ Paie</th>
                  <th className="py-2 pr-3 text-right">→ RTT</th>
                  <th className="py-2 text-right">En attente</th>
                </tr>
              </thead>
              <tbody>
                {compteurs.map((c) => (
                  <tr key={c.agentUserId} className="border-b border-border/60">
                    <td className="py-2 pr-3"><RhWho nom={`${c.agentPrenom} ${c.agentNom}`} /></td>
                    <td className="py-2 pr-3 text-right tabular-nums font-semibold text-ink">{fmtMin(c.minutesValidees)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">{fmtMin(c.minutesPaie)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">{fmtMin(c.minutesRtt)}</td>
                    <td className="py-2 text-right tabular-nums text-ink-subtle">{fmtMin(c.minutesEnAttente)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Panneau Absences : demandes à valider (manager)
// ============================================================================
function RhAbsencesPanel() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  // Mode de validation de la commune : décide si l'utilisateur tranche ou donne
  // seulement son avis. Le calcul vit dans le hook — il dépend du mode ET du
  // rôle, et cet écran sert aussi bien le manager que la RH et le maire.
  const { avisSeulement } = useModeValidationConges();
  const [aValider, setAValider] = useState<RhAbsence[]>([]);
  const [traitees, setTraitees] = useState<RhAbsence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // demande en cours de validation (ouvre le choix du type final)
  const [valider, setValider] = useState<RhAbsence | null>(null);
  const [typeValide, setTypeValide] = useState<'conges_payes' | 'justifiee' | 'injustifiee'>('conges_payes');
  const [compteFeries, setCompteFeries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canalAlerte, setCanalAlerte] = useState<'cloche' | 'email' | 'les_deux'>('les_deux');

  async function charger() {
    setLoading(true); setError(null);
    try {
      const [att, val, ref] = await Promise.all([
        api.rh.listerAbsences('en_attente'),
        api.rh.listerAbsences('validee'),
        api.rh.listerAbsences('refusee'),
      ]);
      setAValider(att);
      setTraitees([...val, ...ref].sort((a, b) => b.dateDebut.localeCompare(a.dateDebut)).slice(0, 20));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(); }, []);
  useEffect(() => { api.rh.getAlerteConfig().then((c) => setCanalAlerte(c.canal)).catch(() => {}); }, []);

  async function changerCanal(c: 'cloche' | 'email' | 'les_deux') {
    setCanalAlerte(c);
    try { await api.rh.setAlerteConfig(c); setMsg("Préférence d'alerte enregistrée."); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  function ouvrirValidation(a: RhAbsence) {
    setValider(a);
    setTypeValide(a.typeDemande === 'conges_payes' ? 'conges_payes' : 'justifiee');
    setCompteFeries(false);
  }
  async function confirmerValidation() {
    if (!valider) return;
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.rh.statuerAbsence(valider.id, { statut: 'validee', typeValide, compteFeries }); rafraichirNotifications();
      setMsg('Absence validée.');
      setValider(null);
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }
  async function refuser(a: RhAbsence) {
    const ok = await confirm({ title: 'Refuser cette demande ?', description: `${a.agentNom} · ${a.dateDebut} → ${a.dateFin}`, confirmLabel: 'Refuser', danger: true });
    if (!ok) return;
    try {
      await api.rh.statuerAbsence(a.id, { statut: 'refusee' }); rafraichirNotifications();
      setMsg('Demande refusée.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  function periode(a: RhAbsence): string {
    const meme = a.dateDebut === a.dateFin;
    const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const base = meme ? fmt(a.dateDebut) : `${fmt(a.dateDebut)} → ${fmt(a.dateFin)}`;
    return base + ((a.demiDebut || a.demiFin) ? ' (½)' : '');
  }
  const TYPE_VALIDE_LABEL: Record<string, string> = { conges_payes: 'Congés payés', justifiee: 'Justifiée', injustifiee: 'Injustifiée' };

  /**
   * Le manager donne son avis sans trancher (mode « le maire / la RH valide »).
   * La demande reste en attente : c'est tout l'objet du mode.
   */
  async function donnerAvis(a: RhAbsence) {
    const avis = await prompt({
      title: 'Donner mon avis',
      description: `${a.agentNom} · ${periode(a)}\n\nVotre avis sera transmis au service RH, qui prendra la décision. La demande reste en attente.`,
      placeholder: 'Ex. : OK pour moi · Attention, il serait seul sur le secteur…',
      multiline: true,
      required: true,
      maxLength: 2000,
      confirmLabel: 'Transmettre mon avis',
    });
    if (avis === null) return;
    setError(null); setMsg(null);
    try {
      await api.rh.avisManagerAbsence(a.id, avis); rafraichirNotifications();
      setMsg('Avis transmis au service RH.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  // Stats
  const aujourd = new Date().toISOString().slice(0, 10);
  const enCours = [...aValider, ...traitees].filter((a) => a.dateDebut <= aujourd && a.dateFin >= aujourd && a.statut === 'validee').length;
  const maladieMois = traitees.filter((a) => a.typeValide === 'justifiee' && a.dateDebut.slice(0, 7) === aujourd.slice(0, 7)).length;

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <RhStats items={[
        { label: 'En attente', value: aValider.length, tone: aValider.length > 0 ? 'rh' : 'default' },
        { label: 'En cours (auj.)', value: enCours },
        { label: 'Justifiées (mois)', value: maladieMois, tone: maladieMois > 0 ? 'warn' : 'default' },
        { label: 'Traitées (récent)', value: traitees.length },
      ]} />

      {/* Justificatifs d'arrêts reçus (canal sécurisé RH) */}
      <RhJustificatifsRecus />

      {/* Réglage : canal d'alerte réaffectation */}
      <Card className="p-4">
        <h2 className="mb-1 font-display text-base font-semibold text-ink">Alerte « tâche à réaffecter »</h2>
        <p className="mb-3 text-xs text-ink-muted">Quand une absence tombe sur des tâches déjà affectées, prévenir le responsable par :</p>
        <div className="flex flex-wrap gap-2">
          {([['cloche', 'Cloche seule'], ['email', 'Email seul'], ['les_deux', 'Cloche + email']] as const).map(([val, lab]) => (
            <button key={val} type="button" onClick={() => changerCanal(val)}
              className={['rounded-r-pill border px-3.5 py-1.5 text-xs font-semibold transition',
                canalAlerte === val ? 'border-tech bg-tech-tint/60 text-tech-fg' : 'border-border-hi bg-surface/40 text-ink-muted hover:text-ink'].join(' ')}>
              {lab}
            </button>
          ))}
        </div>
      </Card>

      {/* À valider */}
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-ink">À valider {aValider.length > 0 && <Badge variant="rh">{aValider.length}</Badge>}</h2>
        {loading ? <p className="text-sm text-ink-muted">Chargement…</p>
          : aValider.length === 0 ? <p className="text-sm text-ink-subtle">Aucune demande d'absence en attente.</p>
          : (
            <ul className="space-y-2">
              {aValider.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-ink">{a.agentNom} · {periode(a)}</div>
                    <div className="text-xs text-ink-muted">
                      Demande : {a.typeDemande === 'conges_payes' ? 'Congés payés' : a.typeDemande === 'rtt' ? 'RTT' : a.typeDemande === 'sans_solde' ? 'Sans solde' : 'Autre'}
                      {a.motif && ` — ${a.motif}`}
                    </div>
                    {/* L'avis déjà donné : la RH doit le voir AVANT de trancher,
                        et savoir qui l'a écrit pour pouvoir le rappeler. */}
                    {a.avisManager && (
                      <div className="mt-1 rounded-r-md border border-border/60 bg-surface/60 px-2 py-1 text-xs text-ink-muted">
                        <span className="font-medium text-ink">Avis{a.avisManagerNom ? ` de ${a.avisManagerNom}` : ''} :</span>{' '}
                        {a.avisManager}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* En mode « le maire / la RH valide », le manager ne tranche
                        pas — mais il n'est pas écarté : c'est lui qui sait si
                        l'équipe peut se passer de l'agent. Afficher « Valider »
                        ici reviendrait à lui montrer un bouton que le serveur
                        refuse (403). */}
                    {avisSeulement ? (
                      <Button variant="secondary" size="sm" onClick={() => void donnerAvis(a)}>
                        {a.avisManager ? 'Modifier mon avis' : 'Donner mon avis'}
                      </Button>
                    ) : (
                      <>
                        <Button variant="secondary" size="sm" onClick={() => ouvrirValidation(a)}>Valider…</Button>
                        <button type="button" onClick={() => void refuser(a)} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Card>

      {/* Historique récent */}
      {traitees.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-ink">Traitées (récent)</h2>
          <ul className="space-y-1.5">
            {traitees.map((a) => (
              <li key={a.id} className="flex items-center justify-between rounded-r-md border border-border/60 px-3 py-1.5 text-sm">
                <span className="text-ink">{a.agentNom} · {periode(a)}</span>
                <span className="flex items-center gap-2">
                  {a.statut === 'validee'
                    ? <Badge variant="success">{a.typeValide ? TYPE_VALIDE_LABEL[a.typeValide] : 'Validée'}{a.joursDecomptes > 0 ? ` · ${a.joursDecomptes}` : ''}</Badge>
                    : <Badge variant="urgent">Refusée</Badge>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Dialog validation : choix du type final */}
      <Dialog open={!!valider} onClose={() => setValider(null)} title="Valider l'absence"
        footer={<>
          <Button variant="ghost" onClick={() => setValider(null)}>Annuler</Button>
          <Button variant="rh" loading={saving} onClick={confirmerValidation}>Valider</Button>
        </>}>
        {valider && (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">{valider.agentNom} · {periode(valider)}</p>
            <Field label="Statut retenu" htmlFor="abs-type">
              <select id="abs-type" value={typeValide} onChange={(e) => setTypeValide(e.target.value as typeof typeValide)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="conges_payes">Congés payés (décompte le solde)</option>
                <option value="justifiee">Absence justifiée (ne décompte pas)</option>
                <option value="injustifiee">Absence injustifiée (ne décompte pas)</option>
              </select>
            </Field>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="checkbox" checked={compteFeries} onChange={(e) => setCompteFeries(e.target.checked)} />
              Compter les jours fériés dans la période (ex. jour de solidarité)
            </label>
            {typeValide === 'conges_payes' && <p className="text-xs text-ink-subtle">Le décompte sera calculé sur les jours ouvrés de la période et déduit du solde de l'agent.</p>}
          </div>
        )}
      </Dialog>
      {dialogElement}
    </div>
  );
}

// ============================================================================
// Panneau « À valider » unifié : heures sup + absences + alerte horaires modifiés
// ============================================================================
const SERVICE_COURT: Record<string, string> = { service_technique: 'Technique', scolaire_cantine: 'Scolaire', grc: 'Communication' };
const TYPE_VALIDE_LBL: Record<string, string> = { conges_payes: 'Congés payés', justifiee: 'Justifiée', injustifiee: 'Injustifiée' };

function RhValiderPanel({ onGoTo }: { onGoTo: (t: RhTab) => void }) {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  // Même règle que dans RhAbsencesPanel : le mode de la commune + le rôle
  // décident si l'utilisateur tranche ou donne son avis (cf. le hook).
  const { avisSeulement } = useModeValidationConges();
  const [heuresSup, setHeuresSup] = useState<RhHeuresSup[]>([]);
  const [absences, setAbsences] = useState<RhAbsence[]>([]);
  const [modifies, setModifies] = useState<RhHoraireModifie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [valider, setValider] = useState<RhAbsence | null>(null);
  const [typeValide, setTypeValide] = useState<'conges_payes' | 'justifiee' | 'injustifiee'>('conges_payes');
  const [compteFeries, setCompteFeries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());

  function toggleSel(id: string) {
    setSelection((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAll() {
    setSelection((prev) => prev.size === modifies.length ? new Set() : new Set(modifies.map((m) => m.id)));
  }
  async function transmettre(items: RhHoraireModifie[]) {
    if (items.length === 0) return;
    setError(null); setMsg(null);
    try {
      const r = await api.rh.transmettreCreneaux(items.map((m) => ({ id: m.id, service: m.service })));
      setMsg(`${r.transmis} créneau(x) transmis à la paie.`);
      setSelection(new Set());
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function ouvrirJustif(absenceId: string) {
    try {
      const { url } = await api.rh.justificatifAbsence(absenceId);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Impossible d\'ouvrir le justificatif'); }
  }

  async function charger() {
    setLoading(true); setError(null);
    try {
      const [hs, abs, mod] = await Promise.all([
        api.rh.listerHeuresSup('en_attente'),
        api.rh.listerAbsences('en_attente'),
        api.rh.horairesModifies().catch(() => []),
      ]);
      setHeuresSup(hs); setAbsences(abs); setModifies(mod);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(); }, []);

  async function statuerHs(it: RhHeuresSup, statut: 'valide' | 'refuse', destination?: 'paie' | 'rtt') {
    setError(null); setMsg(null);
    try { await api.rh.statuerHeuresSup(it.id, { statut, destination }); setMsg('Heures traitées.'); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  function periode(a: RhAbsence): string {
    const meme = a.dateDebut === a.dateFin;
    const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return (meme ? fmt(a.dateDebut) : `${fmt(a.dateDebut)} → ${fmt(a.dateFin)}`) + ((a.demiDebut || a.demiFin) ? ' (½)' : '');
  }
  async function confirmerAbsence() {
    if (!valider) return;
    setSaving(true); setError(null); setMsg(null);
    try { await api.rh.statuerAbsence(valider.id, { statut: 'validee', typeValide, compteFeries }); rafraichirNotifications(); setMsg('Absence validée.'); setValider(null); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }
  /**
   * Le manager donne son avis sans trancher (mode « le maire / la RH valide »).
   * La demande RESTE en attente : l'agent ne sera prévenu qu'à la décision de la
   * RH, jamais sur un simple avis.
   */
  async function donnerAvisAbsence(a: RhAbsence) {
    const avis = await prompt({
      title: 'Donner mon avis',
      description: `${a.agentNom} · ${periode(a)}\n\nVotre avis sera transmis au service RH, qui prendra la décision. La demande reste en attente.`,
      placeholder: 'Ex. : OK pour moi · Attention, il serait seul sur le secteur…',
      multiline: true,
      required: true,
      maxLength: 2000,
      confirmLabel: 'Transmettre mon avis',
    });
    if (avis === null) return;
    setError(null); setMsg(null);
    try {
      await api.rh.avisManagerAbsence(a.id, avis); rafraichirNotifications();
      setMsg('Avis transmis au service RH.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function refuserAbsence(a: RhAbsence) {
    const ok = await confirm({ title: 'Refuser cette demande ?', description: `${a.agentNom} · ${periode(a)}`, confirmLabel: 'Refuser', danger: true });
    if (!ok) return;
    try { await api.rh.statuerAbsence(a.id, { statut: 'refusee' }); rafraichirNotifications(); setMsg('Demande refusée.'); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const rien = heuresSup.length === 0 && absences.length === 0;

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Bande de stats */}
      <RhStats items={[
        { label: 'Horaires modifiés', value: modifies.length, sub: 'non transmis paie', tone: modifies.length > 0 ? 'warn' : 'default' },
        { label: 'Heures sup', value: heuresSup.length, sub: 'à valider', tone: heuresSup.length > 0 ? 'rh' : 'default' },
        { label: 'Absences', value: absences.length, sub: 'en attente' },
      ]} />

      {/* Notes de frais à valider (N1 manager) */}
      <RhNotesFraisAValider />

      {/* Horaires modifiés non transmis — cases à cocher + transmission */}
      {modifies.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
            <Badge variant="urgent">{modifies.length}</Badge>
            <h2 className="font-display text-sm font-semibold text-ink">Horaires modifiés non transmis à la paie</h2>
            <Button variant="rh" size="sm" className="ml-auto" onClick={() => void transmettre(modifies)}>Tout transmettre à la paie</Button>
          </div>
          {selection.size > 0 && (
            <div className="flex items-center gap-3 border-b border-border bg-rh-tint/40 px-4 py-2 text-sm">
              <span className="text-ink"><strong>{selection.size}</strong> sélectionné(s)</span>
              <Button variant="secondary" size="sm" onClick={() => void transmettre(modifies.filter((m) => selection.has(m.id)))}>Transmettre à la paie</Button>
              <button type="button" onClick={() => setSelection(new Set())} className="text-xs text-ink-subtle hover:text-ink">Désélectionner</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5" style={{ width: 34 }}><input type="checkbox" checked={selection.size === modifies.length && modifies.length > 0} onChange={toggleAll} /></th>
                  <th className="px-4 py-2.5 font-medium">Agent</th>
                  <th className="px-4 py-2.5 font-medium">Service</th>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Créneau</th>
                  <th className="px-4 py-2.5 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {modifies.map((m) => (
                  <tr key={m.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40">
                    <td className="px-4 py-2.5"><input type="checkbox" checked={selection.has(m.id)} onChange={() => toggleSel(m.id)} /></td>
                    <td className="px-4 py-2.5"><RhWho nom={m.agentNom} /></td>
                    <td className="px-4 py-2.5"><RhServicePill service={m.service} /></td>
                    <td className="px-4 py-2.5 text-ink-muted">{new Date(m.dateDebut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{m.titre}</td>
                    <td className="px-4 py-2.5 text-right"><Button variant="secondary" size="sm" onClick={() => void transmettre([m])}>Transmettre</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {loading ? <p className="text-sm text-ink-muted">Chargement…</p> : (
      <>
      {/* Heures sup à valider — tableau */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          {heuresSup.length > 0 && <Badge variant="info">{heuresSup.length}</Badge>}
          <h2 className="font-display text-sm font-semibold text-ink">Heures supplémentaires à valider</h2>
          <button type="button" onClick={() => onGoTo('heuressup')} className="ml-auto text-xs text-ink-subtle hover:text-ink">Voir les compteurs →</button>
        </div>
        {heuresSup.length === 0 ? <p className="px-4 py-5 text-center text-sm text-ink-subtle">Rien à valider.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Agent</th>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 text-right font-medium">Total</th>
                  <th className="px-4 py-2.5 font-medium">Motif</th>
                  <th className="px-4 py-2.5 text-right font-medium">Décision</th>
                </tr>
              </thead>
              <tbody>
                {heuresSup.map((it) => (
                  <tr key={it.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40">
                    <td className="px-4 py-2.5"><RhWho nom={it.agentNom} /></td>
                    <td className="px-4 py-2.5 text-ink-muted">{new Date(it.dateJour).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-ink">{fmtMin(it.totalMinutes)}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{it.motif || '—'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="secondary" size="sm" onClick={() => void statuerHs(it, 'valide', 'paie')}>✓ Payer</Button>
                        <Button variant="secondary" size="sm" onClick={() => void statuerHs(it, 'valide', 'rtt')}>→ RTT</Button>
                        <button type="button" onClick={() => void statuerHs(it, 'refuse')} className="text-xs text-ink-subtle hover:text-danger">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Absences à valider — tableau */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          {absences.length > 0 && <Badge variant="info">{absences.length}</Badge>}
          <h2 className="font-display text-sm font-semibold text-ink">Absences à valider</h2>
        </div>
        {absences.length === 0 ? <p className="px-4 py-5 text-center text-sm text-ink-subtle">Rien à valider.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Agent</th>
                  <th className="px-4 py-2.5 font-medium">Période</th>
                  <th className="px-4 py-2.5 font-medium">Type demandé</th>
                  <th className="px-4 py-2.5 font-medium">Justificatif</th>
                  <th className="px-4 py-2.5 font-medium">Motif</th>
                  <th className="px-4 py-2.5 text-right font-medium">Décision</th>
                </tr>
              </thead>
              <tbody>
                {absences.map((a) => (
                  <tr key={a.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40">
                    <td className="px-4 py-2.5"><RhWho nom={a.agentNom} /></td>
                    <td className="px-4 py-2.5 text-ink-muted">{periode(a)}</td>
                    <td className="px-4 py-2.5"><Badge variant="neutral">{a.typeDemande === 'conges_payes' ? 'Congés payés' : a.typeDemande === 'rtt' ? 'RTT' : a.typeDemande === 'sans_solde' ? 'Sans solde' : 'Autre'}</Badge></td>
                    <td className="px-4 py-2.5">{a.aJustificatif
                      ? <button type="button" onClick={() => void ouvrirJustif(a.id)} className="text-xs text-rh underline hover:opacity-80">{a.justificatifNom ? (a.justificatifNom.length > 18 ? a.justificatifNom.slice(0, 16) + '…' : a.justificatifNom) : 'Voir la pièce'}</button>
                      : <span className="text-ink-subtle">—</span>}</td>
                    <td className="px-4 py-2.5 text-ink-muted">
                      {a.motif || '—'}
                      {/* L'avis du manager : la RH doit le lire AVANT de trancher. */}
                      {a.avisManager && (
                        <div className="mt-1 text-xs">
                          <span className="font-medium text-ink">Avis{a.avisManagerNom ? ` de ${a.avisManagerNom}` : ''} :</span>{' '}
                          <span className="text-ink-muted">{a.avisManager}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Mode « le maire / la RH valide » : le manager ne
                            tranche pas. Lui montrer « Valider » serait lui
                            promettre un geste que le serveur refuse (403). */}
                        {avisSeulement ? (
                          <Button variant="secondary" size="sm" onClick={() => void donnerAvisAbsence(a)}>
                            {a.avisManager ? 'Modifier mon avis' : 'Donner mon avis'}
                          </Button>
                        ) : (
                          <>
                            <Button variant="secondary" size="sm" onClick={() => { setValider(a); setTypeValide(a.typeDemande === 'conges_payes' ? 'conges_payes' : 'justifiee'); setCompteFeries(false); }}>Valider…</Button>
                            <button type="button" onClick={() => void refuserAbsence(a)} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {rien && modifies.length === 0 && <p className="text-center text-sm text-ink-subtle">Tout est à jour : rien en attente de validation.</p>}
      </>
      )}

      {/* Dialog validation absence */}
      <Dialog open={!!valider} onClose={() => setValider(null)} title="Valider l'absence"
        footer={<>
          <Button variant="ghost" onClick={() => setValider(null)}>Annuler</Button>
          <Button variant="rh" loading={saving} onClick={confirmerAbsence}>Valider</Button>
        </>}>
        {valider && (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">{valider.agentNom} · {periode(valider)}</p>
            <Field label="Statut retenu" htmlFor="av-type">
              <select id="av-type" value={typeValide} onChange={(e) => setTypeValide(e.target.value as typeof typeValide)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="conges_payes">Congés payés (décompte le solde)</option>
                <option value="justifiee">Absence justifiée (ne décompte pas)</option>
                <option value="injustifiee">Absence injustifiée (ne décompte pas)</option>
              </select>
            </Field>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="checkbox" checked={compteFeries} onChange={(e) => setCompteFeries(e.target.checked)} />
              Compter les jours fériés (jour de solidarité)
            </label>
          </div>
        )}
      </Dialog>
      {dialogElement}
    </div>
  );
}

// ============================================================================
// Panneau « Mes demandes » : espace personnel (heures sup + absences), tous rôles
// ============================================================================
function MesDemandesPanel() {
  const [hs, setHs] = useState<RhHeuresSup[]>([]);
  const [abs, setAbs] = useState<RhAbsence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // formulaire heures sup
  const today = new Date().toISOString().slice(0, 10);
  const [hsDialog, setHsDialog] = useState(false);
  const [hsDate, setHsDate] = useState(today);
  const [hHM, setHHM] = useState('0'); const [mHM, setMHM] = useState('0');
  const [hHA, setHHA] = useState('0'); const [mHA, setMHA] = useState('0');
  const [hsMotif, setHsMotif] = useState('');

  // formulaire absence
  const [absDialog, setAbsDialog] = useState(false);
  const [absDebut, setAbsDebut] = useState(today);
  const [absFin, setAbsFin] = useState(today);
  const [absDemiD, setAbsDemiD] = useState(false);
  const [absDemiF, setAbsDemiF] = useState(false);
  const [absType, setAbsType] = useState('conges_payes');
  const [absMotif, setAbsMotif] = useState('');
  const [absPiece, setAbsPiece] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  // M27 (retour Manon) — demande de FORMATION : un souhait instruit par la RH,
  // pas une absence (aucun solde touché). Catalogue de la commune ou libre.
  const [catalogue, setCatalogue] = useState<Array<{ id: string; intitule: string; organisme: string; domaine: string | null }>>([]);
  const [mesFormations, setMesFormations] = useState<DemandeFormationDTO[]>([]);
  const [fCatalogue, setFCatalogue] = useState('');
  const [fLibre, setFLibre] = useState('');
  const [fDu, setFDu] = useState('');
  const [fAu, setFAu] = useState('');
  const [fCommentaire, setFCommentaire] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [a, b, f, c] = await Promise.all([
        api.rh.mesHeuresSup(), api.rh.mesAbsences(),
        api.rh.mesDemandesFormation().catch(() => []),
        api.rh.catalogueFormationsActif().catch(() => []),
      ]);
      setHs(a); setAbs(b); setMesFormations(f); setCatalogue(c);
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function declarerHs() {
    const mm = Number(hHM) * 60 + Number(mHM); const ma = Number(hHA) * 60 + Number(mHA);
    if (mm + ma <= 0) { setError('Renseigne au moins une durée'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.rh.declarerHeuresSup({ dateJour: hsDate, minutesMatin: mm, minutesApresMidi: ma, motif: hsMotif.trim() || undefined });
      setHsDialog(false); setHHM('0'); setMHM('0'); setHHA('0'); setMHA('0'); setHsMotif(''); setMsg('Heures déclarées.'); await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }
  async function demanderAbs() {
    if (absType === 'formation') { await demanderFormation(); return; }
    if (absFin < absDebut) { setError('La date de fin doit être après le début'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      const piece = absPiece ? await compressImageIfNeeded(absPiece) : undefined;
      await api.rh.demanderAbsence({ dateDebut: absDebut, dateFin: absFin, demiDebut: absDemiD, demiFin: absDemiF, typeDemande: absType, motif: absMotif.trim() || undefined }, piece); rafraichirNotifications();
      setAbsDialog(false); setAbsMotif(''); setAbsPiece(null); setMsg('Demande envoyée.'); await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  async function demanderFormation() {
    if (!fCatalogue && fLibre.trim().length < 3) { setError('Choisissez une formation du catalogue ou saisissez un intitulé'); return; }
    if (fDu && fAu && fAu < fDu) { setError('La fin de la période souhaitée doit suivre son début'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.rh.demanderFormation({
        catalogueId: fCatalogue || null, intituleLibre: fCatalogue ? null : fLibre.trim(),
        periodeDu: fDu || null, periodeAu: fAu || null, commentaire: fCommentaire.trim() || null,
      });
      rafraichirNotifications();
      setAbsDialog(false); setFCatalogue(''); setFLibre(''); setFDu(''); setFAu(''); setFCommentaire(''); setAbsType('conges_payes');
      setMsg('Demande de formation envoyée à la RH.'); await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  const STA: Record<string, { label: string; v?: 'success' | 'urgent' }> = {
    en_attente: { label: 'En attente' }, valide: { label: 'Validé', v: 'success' }, refuse: { label: 'Refusé', v: 'urgent' },
    validee: { label: 'Validée', v: 'success' }, refusee: { label: 'Refusée', v: 'urgent' },
    acceptee: { label: 'Acceptée', v: 'success' }, annulee: { label: 'Annulée' },
  };
  const selStyle = 'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink';

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Transmettre un arrêt de travail (canal sécurisé RH) */}
      <RhTransmettreArret />

      {/* Heures sup */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-ink">Mes heures supplémentaires</h2>
          <Button variant="rh" size="sm" onClick={() => setHsDialog(true)}>Déclarer</Button>
        </div>
        {loading ? <p className="text-sm text-ink-muted">Chargement…</p>
          : hs.length === 0 ? <p className="text-sm text-ink-subtle">Aucune déclaration.</p>
          : (
            <ul className="space-y-1.5">
              {hs.map((it) => {
                const st = STA[it.statut] ?? { label: it.statut };
                return (
                  <li key={it.id} className="flex items-center justify-between rounded-r-md border border-border/60 px-3 py-2 text-sm">
                    <span className="text-ink">{new Date(it.dateJour).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · <strong>{fmtMin(it.totalMinutes)}</strong>{it.motif && <span className="text-ink-subtle"> — {it.motif}</span>}</span>
                    <span className="flex items-center gap-2">
                      {it.statut === 'valide' && it.destination && <span className="text-xs text-ink-subtle">{it.destination === 'paie' ? 'payé' : 'RTT'}</span>}
                      <Badge variant={st.v}>{st.label}</Badge>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
      </Card>

      {/* Absences */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-ink">Mes absences</h2>
          <Button variant="rh" size="sm" onClick={() => setAbsDialog(true)}>Demander</Button>
        </div>
        {loading ? <p className="text-sm text-ink-muted">Chargement…</p>
          : abs.length === 0 ? <p className="text-sm text-ink-subtle">Aucune demande.</p>
          : (
            <ul className="space-y-1.5">
              {abs.map((a) => {
                const st = STA[a.statut] ?? { label: a.statut };
                const meme = a.dateDebut === a.dateFin;
                const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                return (
                  <li key={a.id} className="flex items-center justify-between rounded-r-md border border-border/60 px-3 py-2 text-sm">
                    <span className="text-ink">{meme ? fmt(a.dateDebut) : `${fmt(a.dateDebut)} → ${fmt(a.dateFin)}`}{(a.demiDebut || a.demiFin) && ' (½)'}{a.statut === 'validee' && a.joursDecomptes > 0 && <span className="text-ink-subtle"> · {a.joursDecomptes} décompté(s)</span>}</span>
                    <Badge variant={st.v}>{st.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
      </Card>

      {/* M27 — mes demandes de formation (souhaits instruits par la RH). */}
      {mesFormations.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-ink">Mes demandes de formation</h2>
          <ul className="space-y-1.5">
            {mesFormations.map((f) => {
              const st = STA[f.statut] ?? { label: f.statut };
              return (
                <li key={f.id} className="rounded-r-md border border-border/60 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ink">{f.intitule}{f.organisme ? <span className="text-ink-subtle"> · {f.organisme}</span> : null}</span>
                    <Badge variant={st.v}>{st.label}</Badge>
                  </div>
                  {(f.periodeDu || f.periodeAu) && (
                    <p className="text-xs text-ink-subtle">Souhaitée {f.periodeDu ? `à partir du ${new Date(f.periodeDu).toLocaleDateString('fr-FR')}` : ''}{f.periodeAu ? ` jusqu'au ${new Date(f.periodeAu).toLocaleDateString('fr-FR')}` : ''}</p>
                  )}
                  {f.reponse && <p className="text-xs text-ink-muted">Réponse : {f.reponse}</p>}
                  {f.statut === 'en_attente' && (
                    <button type="button" className="mt-1 text-xs text-ink-subtle hover:text-urgent"
                      onClick={() => void api.rh.annulerDemandeFormation(f.id).then(load)}>Annuler ma demande</button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* M28 (retour Manon) — formation → ordre de mission → note de frais, au même endroit. */}
      <MesDeplacementsCard demandesFormation={mesFormations} onChange={rafraichirNotifications} />

      {/* Dialog heures sup */}
      <Dialog open={hsDialog} onClose={() => setHsDialog(false)} title="Déclarer des heures supplémentaires"
        footer={<><Button variant="ghost" onClick={() => setHsDialog(false)}>Annuler</Button><Button variant="rh" loading={saving} onClick={declarerHs}>Déclarer</Button></>}>
        <div className="space-y-4">
          <Field label="Jour" htmlFor="md-hs-date" required><Input id="md-hs-date" type="date" value={hsDate} onChange={(e) => setHsDate(e.target.value)} /></Field>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink">Matin</span>
            <div className="flex items-center gap-2"><Input type="number" min={0} max={12} value={hHM} onChange={(e) => setHHM(e.target.value)} className="w-20" /> <span className="text-sm text-ink-muted">h</span><Input type="number" min={0} max={59} value={mHM} onChange={(e) => setMHM(e.target.value)} className="w-20" /> <span className="text-sm text-ink-muted">min</span></div>
          </div>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink">Après-midi</span>
            <div className="flex items-center gap-2"><Input type="number" min={0} max={12} value={hHA} onChange={(e) => setHHA(e.target.value)} className="w-20" /> <span className="text-sm text-ink-muted">h</span><Input type="number" min={0} max={59} value={mHA} onChange={(e) => setMHA(e.target.value)} className="w-20" /> <span className="text-sm text-ink-muted">min</span></div>
          </div>
          <Field label="Motif (facultatif)" htmlFor="md-hs-motif"><Input id="md-hs-motif" value={hsMotif} onChange={(e) => setHsMotif(e.target.value)} /></Field>
        </div>
      </Dialog>

      {/* Dialog absence */}
      <Dialog open={absDialog} onClose={() => setAbsDialog(false)} title={absType === 'formation' ? 'Demander une formation' : 'Demander une absence'}
        footer={<><Button variant="ghost" onClick={() => setAbsDialog(false)}>Annuler</Button><Button variant="rh" loading={saving} onClick={demanderAbs}>Envoyer</Button></>}>
        <div className="space-y-4">
          <Field label="Type" htmlFor="md-ab-type">
            <select id="md-ab-type" value={absType} onChange={(e) => setAbsType(e.target.value)} className={selStyle}>
              <option value="conges_payes">Congés payés</option>
              <option value="rtt">RTT / récupération</option>
              <option value="sans_solde">Sans solde</option>
              {/* M27 — une formation est un souhait instruit par la RH, pas une absence. */}
              <option value="formation">Formation</option>
              <option value="autre">Autre</option>
            </select>
          </Field>

          {absType === 'formation' ? (
            <>
              <Field label="Formation souhaitée" htmlFor="md-fo-cat">
                <select id="md-fo-cat" value={fCatalogue} onChange={(e) => setFCatalogue(e.target.value)} className={selStyle}>
                  <option value="">{catalogue.length ? '— Choisir dans le catalogue de la mairie —' : '— Catalogue vide : saisissez l’intitulé ci-dessous —'}</option>
                  {catalogue.map((c) => (
                    <option key={c.id} value={c.id}>{c.domaine ? `${c.domaine} — ` : ''}{c.intitule} ({c.organisme})</option>
                  ))}
                </select>
              </Field>
              {!fCatalogue && (
                <Field label="Ou intitulé libre" htmlFor="md-fo-libre">
                  <Input id="md-fo-libre" value={fLibre} maxLength={300} placeholder="Ex : langue des signes, niveau 1" onChange={(e) => setFLibre(e.target.value)} />
                </Field>
              )}
              <div>
                <p className="mb-1 text-sm font-medium text-ink">Période souhaitée <span className="font-normal text-ink-subtle">(indicative, aussi longue que vous voulez)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="À partir du" htmlFor="md-fo-du"><Input id="md-fo-du" type="date" value={fDu} onChange={(e) => setFDu(e.target.value)} /></Field>
                  <Field label="Jusqu'au" htmlFor="md-fo-au"><Input id="md-fo-au" type="date" value={fAu} onChange={(e) => setFAu(e.target.value)} /></Field>
                </div>
              </div>
              <Field label="Commentaire (facultatif)" htmlFor="md-fo-com"><Input id="md-fo-com" value={fCommentaire} maxLength={2000} placeholder="Pourquoi cette formation ?" onChange={(e) => setFCommentaire(e.target.value)} /></Field>
              <p className="text-xs text-ink-subtle">La demande part à la RH et à votre responsable. Elle ne décompte aucun jour de congé.</p>
            </>
          ) : (<>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du" htmlFor="md-ab-d" required><Input id="md-ab-d" type="date" value={absDebut} onChange={(e) => setAbsDebut(e.target.value)} /></Field>
            <Field label="Au" htmlFor="md-ab-f" required><Input id="md-ab-f" type="date" value={absFin} onChange={(e) => setAbsFin(e.target.value)} /></Field>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-sm text-ink-muted"><input type="checkbox" checked={absDemiD} onChange={(e) => setAbsDemiD(e.target.checked)} /> Commence l'après-midi</label>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted"><input type="checkbox" checked={absDemiF} onChange={(e) => setAbsDemiF(e.target.checked)} /> Finit le matin</label>
          </div>
          <Field label="Motif (facultatif)" htmlFor="md-ab-motif"><Input id="md-ab-motif" value={absMotif} onChange={(e) => setAbsMotif(e.target.value)} /></Field>
          <Field label="Justificatif (facultatif — PDF ou photo)" htmlFor="md-ab-piece">
            <input id="md-ab-piece" type="file" accept=".pdf,image/*" onChange={(e) => setAbsPiece(e.target.files?.[0] ?? null)} className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-r-md file:border-0 file:bg-rh-tint file:px-3 file:py-1.5 file:text-rh-fg" />
          </Field>
          </>)}
        </div>
      </Dialog>
    </div>
  );
}
